import { NextRouter } from 'next/router';
import { JsBridge } from './js-bridge/js-bridge';
import { ButtonFragment, ContentBlockFragment } from '@graphqlTypes/sitecore';
import { ContentBlockWithSection } from '@mobx/stores/calendar-store';
import { getSearchParams } from './query-string';
import { DiscoverQueryParams } from '@src/common/enums';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import { TrackingEvent } from '@src/adobe-launch/types';
import { convertArticleInfo } from '@src/hooks/tracker/use-article-tracker';
import loadingStore from '@mobx/stores/loading-store';
import isSharePageStore from '@mobx/stores/isShare-store';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const isServer = typeof window === 'undefined';

const cid = 'OSXXIOA_O2ODiscover_SE_3in1_CS';
const isAddToCalendarCid = 'OSXXIOA_O2ODiscover_SE_3in1_CD';

/** 设备版本识别 */
const UAParser = require('ua-parser-js');
const parser = new UAParser();
const result = parser.getResult();

/** 通用处理share URL 非deepLink 处理 */
export const isShareUrl = (
    blockType: string | undefined,
    defaultUrl: string,
    raceLink: string | undefined,
    isAddToCalendar?: boolean
) => {
    const link: string =
        blockType === 'BetPick'
            ? raceLink
                ? `${raceLink.split('/').slice(5).join('/')}/`
                : defaultUrl
            : defaultUrl;
    const baseShareUrlMap: Record<string, string> = {
        'https://special.iuww.com': 'https://sh.iuww.com/d',
        'https://special.qcew.com': 'https://sh.qcew.com/d',
        'https://special.hkjc.com': 'https://sh.hkjc.com/d',
    };

    const baseShareUrl = baseShareUrlMap[location.origin] || `https://${location.host}/discover`;
    const pathSegment = location.pathname.split('/')[2];
    //calendar entry will return homepage
    if (blockType === 'calendarEntry') {
        return `${baseShareUrl}/${pathSegment}/share/${isDeepLinkShareUrl(
            '',
            isAddToCalendar ?? false
        )}`;
    }
    return `${baseShareUrl}/${pathSegment}/share/${isDefShareUrl(link, isAddToCalendar ?? false)}`;
};

/** 通用share 处理 */
export const isDefShareUrl = (url: string, isAddToCalendar?: boolean) => {
    const hasQueryString = /\?.*=/.test(url);
    return hasQueryString
        ? `${url}&cid=${encodeURIComponent(isAddToCalendar ? isAddToCalendarCid : cid)}`
        : `${url}?cid=${encodeURIComponent(isAddToCalendar ? isAddToCalendarCid : cid)}`;
};

/** deepLink share url 处理 */
export const isDeepLinkShareUrl = (url: string, isAddToCalendar?: boolean) => {
    const hasQueryString = (url.match(/\?/g) || []).length;

    return hasQueryString >= 2
        ? `${url}&cid=${encodeURIComponent(isAddToCalendar ? isAddToCalendarCid : cid)}`
        : `${url}?cid=${encodeURIComponent(isAddToCalendar ? isAddToCalendarCid : cid)}`;
};

export const isCalendarShareUrl = (defaultUrl: string) => {
    // SIT https://sh.iuww.com/d/ 无域名重定向，所以直接拼接special.iuww.com域名
    if (location.origin == 'https://special.iuww.com') {
        return `https://sh.iuww.com/d/${location.pathname.split('/')[2]}/share/${defaultUrl}`;
        // return `https://special.iuww.com/discover/${
        //     location.pathname.split('/')[2]
        // }/share/${defaultUrl}`;
    } else if (location.origin == 'https://special.qcew.com') {
        return `https://sh.qcew.com/d/${location.pathname.split('/')[2]}/share/${defaultUrl}`;
    } else if (location.origin == 'https://special.hkjc.com') {
        return `https://sh.hkjc.com/d/${location.pathname.split('/')[2]}/share/${defaultUrl}`;
    } else {
        return `https://${location.host}/discover/${
            location.pathname.split('/')[2]
        }/share/${defaultUrl}`;
    }
};
/** 过滤空对象 */
export const objFilter = (arr: any) => {
    const newArr = arr.filter((i: any) => Object.keys(i).length !== 0);
    return newArr;
};

/** 是否在时间范围内
 * display----开关
 * startDate--开始时间
 * endDate----结束时间
 */
export const isTimeFrame = (display: boolean, startDate: number, endDate: number) => {
    if (!display) return false;
    if (startDate <= 0 && endDate <= 0) {
        return true;
    }
    if (endDate < 0) {
        endDate = Number.MAX_SAFE_INTEGER;
    }
    const date = new Date().getTime();
    return date >= startDate && date < endDate;
};

/** 用于处理hashTags 逻辑 */
export const contentArr = (filterVal: string, arr: any) => {
    const all: any = [];
    if (filterVal !== 'all') {
        arr?.contentBlocks.forEach((i: any, index: number) => {
            i.hashTags &&
                i.hashTags.tags &&
                i.hashTags.tags.length > 0 &&
                i.hashTags.tags.forEach((n: any) => {
                    if (n.name === filterVal) {
                        all.push(arr?.contentBlocks[index]);
                    }
                });
        });
    }
    let blocks = filterVal === 'all' ? arr?.contentBlocks : all;
    const numberOfContent = arr?.numberOfContent?.numberValue;
    blocks = blocks.slice(0, numberOfContent);
    return blocks;
};

export const getQueryVariable = (variable: string) => {
    const g = isServer ? global : window;
    const query = g.location?.search.substring(1);
    const v = query?.split('&');
    for (let i = 0; i < v?.length; i++) {
        const pair = v[i].split('=');
        if (pair[0] == variable) {
            return pair[1];
        }
    }
    return false;
};

export const isDuringDate = (startDate: number, endDate: number) => {
    const curDate = new Date();
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (curDate >= s && curDate <= e) {
        return true;
    } else {
        return false;
    }
};

const n = isServer ? global.navigator : window.navigator;
const u = isServer ? global.navigator?.userAgent : window.navigator?.userAgent;
/** 是否安卓 */
export const isAndroid = /Android/.test(u);
/** 是否ios */
export const isiOS =
    /iPad|iPhone|iPod/.test(u) && n.platform === 'MacIntel' && n.maxTouchPoints > 1;

/** 是否安卓--share页面 */
export const isAndroidShare = (u && u.indexOf('Android') > -1) || (u && u.indexOf('Adr') > -1);
/** 是否ios--share页面 */
export const isiOSShare = u && !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);

/** 字符串转换对象
 * '?name=Zack&age=18' -> {name: 'Zack', age: '18'}
 */
export const queryString = (search: string): Record<string, string> => {
    if (!search) {
        return {};
    }

    return search
        .slice(1) // 去掉开头的 '?'
        .split('&')
        .reduce<Record<string, string>>((acc, param) => {
            const [key, value = ''] = param.split('=');
            return {
                ...acc,
                [decodeURIComponent(key)]: decodeURIComponent(value || ''),
            };
        }, {});
};

/**
 * 去掉search内的cid和b_cid
 * ?log=1&cid=OSXXIOA_O2ODiscover_SE_3in1_BN&seg=12 -----> ?log=1&seg=12
 */
export const removeCIDUrl = (url: string) => {
    const isSharePage = isSharePageStore.isSharePage;
    if (isSharePage) {
        return '';
    }
    let parsedUrl;

    try {
        parsedUrl = new URL(
            url,
            typeof window !== 'undefined' && window.location && window.location.origin
                ? window.location.origin
                : ''
        );
    } catch (error) {
        console.error('Invalid URL:', url, error);
        return url; // 或者返回其他默认值
    }
    const params = new URLSearchParams(parsedUrl.search);
    params.delete('cid');
    params.delete('b_cid');
    const newSearch = params.toString();
    const baseUrl = `${parsedUrl.pathname}`;
    return newSearch ? `${baseUrl}?${newSearch}` : baseUrl;
};

/** 跳转外链逻辑---	ExternalHashId:true  */
export const externalLink = (
    url: string,
    hashId: string,
    ac: [] | string[],
    seg: [] | string[]
) => {
    const urlParams = new URLSearchParams();

    if (hashId) {
        urlParams.append('guid', hashId);
    }

    ac.forEach((param) => urlParams.append('ac', param));
    seg.forEach((param) => urlParams.append('seg', param));

    const paramsString = urlParams.toString();

    if (paramsString) {
        return url.includes('?') ? `${url}&${paramsString}` : `${url}?${paramsString}`;
    } else {
        return url;
    }
};

export const blockTypeLink = (
    blockType: string | undefined,
    router: NextRouter,
    link: string | undefined,
    jsBridge: JsBridge
) => {
    if (blockType === 'Video') {
        return;
    } else if (blockType === 'External') {
        jsBridge.appOpenExternalBrowser(link || '');
    } else {
        router.push(`${link}`).then(() => {
            jsBridge.appSetUiVisibility('btnBack', true);
        });
    }
};

/** 根据blockType类型 整合跳转Link */
export const blockTypeLinkTo = (
    i: ContentBlockFragment | ContentBlockWithSection | ButtonFragment,
    generalLink: string,
    router: NextRouter,
    jsBridge: JsBridge,
    settingStore: any
) => {
    const blockType = i.blockType?.value;
    if (blockType === 'Video') {
        return;
    }
    const searchParams = getSearchParams();
    const ac = searchParams.getAll(DiscoverQueryParams.account);
    const seg = searchParams.getAll(DiscoverQueryParams.segment);
    let ExternalLink = '';
    let BetPickLink = '';

    switch (blockType) {
        case 'General':
            showLoading();
            router.push(generalLink ?? '').then(() => {
                jsBridge.appSetUiVisibility('btnBack', true);
            });

            break;

        case 'BetPick':
            showLoading();
            BetPickLink = i.raceLink?.targetItem?.path
                ? removeCIDUrl(
                      `/${i.raceLink?.targetItem?.path.split('/').slice(5).join('/')}${
                          location.search
                      }`
                  )
                : '';
            router.push(BetPickLink).then(() => {
                jsBridge.appSetUiVisibility('btnBack', true);
            });

            break;

        case 'Internal':
            showLoading();
            router.push(i.internalLink?.value ?? '').then(() => {
                jsBridge.appSetUiVisibility('btnBack', true);
            });

            break;

        case 'External':
            ExternalLink = externalLink(
                i.externalLink?.value ?? '',
                i.externalHashId?.boolValue ? settingStore.wid ?? '' : '',
                i.externalAc?.boolValue ? ac : [],
                i.externalSeg?.boolValue ? seg : []
            );
            jsBridge.appOpenExternalBrowser(ExternalLink || '');
            break;

        default:
            showLoading();
            router.push(generalLink ?? '').then(() => {
                jsBridge.appSetUiVisibility('btnBack', true);
            });

            break;
    }
};

/** 判断blockType为 Video*/
export const isVideoPop = (i: ContentBlockFragment | ContentBlockWithSection | ButtonFragment) => {
    return i.blockType?.value === 'Video' && !i.videoNative?.boolValue ? 'video-pop' : '';
};

/** videoNative-jsBridge */
export const videoNative = (
    i: ContentBlockFragment | ContentBlockWithSection | ButtonFragment,
    jsBridge: JsBridge
) => {
    if (i.blockType?.value === 'Video' && i.videoNative?.boolValue) {
        nonPageTracker(
            {
                eventID: TrackingEvent.VideoPlay,
                videoURL: removeflashUrl(i.videoLink?.value ?? ''),
            },
            { article: convertArticleInfo(i) }
        );
    }

    return (
        i.blockType?.value === 'Video' &&
        i.videoNative?.boolValue &&
        jsBridge.appOpenNativePlayer(
            i.videoLink?.value ?? '',
            i.videoTitle?.value ?? '',
            i.videoNativePip?.boolValue ?? false
        )
    );
};

export function withFullPath(path?: string): string {
    if (!path) {
        return path || '';
    }
    if (path.startsWith('http:') || path.startsWith('https:')) {
        return path;
    }
    const MEDIA_FULL_PATH = /(?=.*\/\/)(?=.*-\/media\/).*$/;
    if (MEDIA_FULL_PATH.test(path)) {
        return `https:${path}`;
    }
    return path;
}

export function withSitPath(path?: string): string {
    if (!path) {
        return path || '';
    }
    if (path.startsWith('http:') || path.startsWith('https:')) {
        return path;
    }

    return path.replace(/^\/\//, '');
}
// get ful img path with single imageName
export function getFullImgPath(imgName: string): string {
    const baseUrlMap: Record<string, string> = {
        'https://special.iuww.com': 'https://www.iuww.com',
        'http://localhost:3000': 'https://www.iuww.com',
        'https://special.qcew.com': 'https://consvc.qcew.com',
        'https://sh.qcew.com': 'https://consvc.qcew.com',
        'https://special.hkjc.com': 'https://consvc.hkjc.com',
        'https://sh.hkjc.com': 'https://consvc.hkjc.com',
    };
    const imgFolder = '/-/media/Sites/Discover/';
    const baseDomain = isServer
        ? ''
        : baseUrlMap[window?.location?.origin] || `https://${window?.location?.host}`;
    // const baseDomain = 'https://www.iuww.com';
    const baseImgURL = baseDomain + imgFolder + imgName;
    return baseImgURL;
}
// change Top Banner backgrand image to webp first
export function withWebpImage(path?: string): string {
    if (!path) {
        return path || '';
    }
    let temp = '';
    const domain = window.location.origin + '/discover/_next/image/?url=';
    if (path.indexOf('.svg') > 0) {
        if (path.startsWith('http:') || path.startsWith('https:')) {
            return path;
        } else return path.replace(/^\/\//, '');
    } else {
        if (path.startsWith('http:') || path.startsWith('https:')) {
            temp = domain + encodeURIComponent(path) + '&w=750&q=75';
        } else {
            temp =
                domain + encodeURIComponent(('https:' + path).replace(/^\/\//, '')) + '&w=750&q=75';
        }
    }
    return temp;
}

/** 去掉特定前缀 https://streaminghkjc-a.akamaihd.net/hdflash/*/
export const removeflashUrl = (url: string) => {
    const parsedUrl = url.replace('https://streaminghkjc-a.akamaihd.net/hdflash/', '');
    return parsedUrl;
};

/** loading 显示 */
export const showLoading = () => {
    return loadingStore.setLoading(true);
};
/** loading 隐藏 */
export const hideLoading = () => {
    return loadingStore.setLoading(false);
};

/** 识别当前是否是IOS16 版本 */
export const isIOS16 = () => {
    const { name, version } = result.os;
    return name === 'iOS' && parseFloat(version) >= 16 && parseFloat(version) < 17;
};

/** 识别当前设备类型
 * - mobile
 * - tablet
 * - desktop
 */
export const getDeviceType = () => {
    return result.device.type;
};

/** 判断是否为 PC 浏览器 */
export const isPC = result.device.type === undefined;

/** IOS不识别“-”，会发生错误
 *  '2024-11-12 09:06:05' --->  '2024/11/12 09:06:05' */
export const formattedServerTime = (v: string) => {
    return v.replace(/-/g, '/');
};
